'use client';

import { useEffect, useState } from 'react';
import { useTranslation } from '@/i18n/LanguageProvider';
import { Heart, Share2, Star } from 'lucide-react';

interface Review {
  userName?: string;
  name?: string;
  displayName?: string;
  visitorName?: string;
  userDisplayName?: string;
  visitorDisplayName?: string;
  rating?: number;
  comment?: string;
  text?: string;
  description?: string;
  createdAt?: { seconds: number } | string;
}

interface TourInteractiveProps {
  tourId: string;
  tourTitle: string;
  tourImage: string;
  section: 'actions' | 'reviews';
}

export default function TourInteractive({ tourId, tourTitle, tourImage, section }: TourInteractiveProps) {
  const { t, locale } = useTranslation();
  const [liked, setLiked] = useState(false);
  const [likeDocId, setLikeDocId] = useState<string | null>(null);
  const [shared, setShared] = useState(false);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    let unsubscribe: (() => void) | null = null;
    const init = async () => {
      const { auth } = await import('@/lib/firebase');
      const { onAuthStateChanged } = await import('firebase/auth');
      unsubscribe = onAuthStateChanged(auth, async (user) => {
        setUserId(user?.uid || null);
        if (user) {
          try {
            const { db } = await import('@/lib/firebase');
            const { collection, query, where, getDocs } = await import('firebase/firestore');
            const q = query(
              collection(db, 'follows'),
              where('userId', '==', user.uid),
              where('targetId', '==', tourId),
              where('type', '==', 'destination')
            );
            const snap = await getDocs(q);
            if (!snap.empty) {
              setLiked(true);
              setLikeDocId(snap.docs[0].id);
            }
          } catch {}
        }
      });
    };
    init();
    return () => { unsubscribe?.(); };
  }, [tourId]);

  // Fetch reviews
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const { db } = await import('@/lib/firebase');
        const { collection, query, where, getDocs } = await import('firebase/firestore');
        const q = query(collection(db, 'reviews'), where('tourId', '==', tourId));
        const snap = await getDocs(q);
        setReviews(snap.docs.map(d => d.data() as Review));
      } catch {}
    };
    fetchReviews();
  }, [tourId]);

  const handleLike = async () => {
    if (!userId) {
      window.location.href = '/login';
      return;
    }
    try {
      const { db } = await import('@/lib/firebase');
      const { doc, deleteDoc, addDoc, collection, Timestamp } = await import('firebase/firestore');
      if (liked && likeDocId) {
        await deleteDoc(doc(db, 'follows', likeDocId));
        setLiked(false);
        setLikeDocId(null);
      } else {
        const docRef = await addDoc(collection(db, 'follows'), {
          userId,
          targetId: tourId,
          type: 'destination',
          targetName: tourTitle,
          targetImage: tourImage,
          createdAt: Timestamp.now(),
          notificationsEnabled: true,
        });
        setLiked(true);
        setLikeDocId(docRef.id);
      }
    } catch {}
  };

  const handleShare = async () => {
    // Compartir la URL actual de la pagina (que ya es el slug bonito).
    const url = window.location.origin + window.location.pathname;
    if (navigator.share) {
      try { await navigator.share({ title: tourTitle, url }); } catch {}
    } else {
      await navigator.clipboard.writeText(url);
      setShared(true);
      setTimeout(() => setShared(false), 2000);
    }
  };

  if (section === 'actions') {
    return (
      <div className="mt-6 pt-6 border-t flex justify-center gap-4">
        <button
          onClick={handleLike}
          aria-label={liked ? 'Unlike' : 'Like'}
          className={`p-2 transition-colors ${liked ? 'text-red-500' : 'text-gray-400 dark:text-gray-500 hover:text-red-500'}`}
        >
          <Heart className="w-6 h-6" fill={liked ? 'currentColor' : 'none'} />
        </button>
        <div className="relative">
          <button
            onClick={handleShare}
            aria-label="Share"
            className="p-2 text-gray-400 dark:text-gray-500 hover:text-primary transition-colors"
          >
            <Share2 className="w-6 h-6" />
          </button>
          {shared && (
            <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
              {t('tour.linkCopied') || 'Link copied!'}
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      {reviews.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
          <h2 className="text-xl font-semibold mb-4">{t('tour.reviews')} ({reviews.length})</h2>
          <div className="space-y-4">
            {reviews.slice(0, 5).map((review, index) => {
              let userName = t('tour.traveler') || 'Traveler';
              const nameFields = ['userName', 'name', 'displayName', 'visitorName', 'userDisplayName', 'visitorDisplayName'] as const;
              for (const field of nameFields) {
                const val = review[field];
                if (val && typeof val === 'string' && val.trim()) {
                  userName = val;
                  break;
                }
              }

              const rating = review.rating || 5;
              const comment = review.comment || review.text || review.description || '';
              const createdAt = review.createdAt;

              let dateStr = t('tour.recently') || 'Recently';
              if (createdAt) {
                try {
                  const date = typeof createdAt === 'object' && 'seconds' in createdAt
                    ? new Date(createdAt.seconds * 1000)
                    : new Date(createdAt as string);
                  dateStr = date.toLocaleDateString(locale);
                } catch {}
              }

              return (
                <div key={index} className="pb-4 border-b last:border-b-0">
                  <div className="flex items-start gap-3 mb-2">
                    <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center text-sm font-semibold text-primary">
                      {userName[0]?.toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900 dark:text-gray-100">{userName}</p>
                      <div className="flex items-center gap-2">
                        <div className="flex">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={`w-4 h-4 ${i < rating ? 'fill-primary text-primary' : 'text-gray-300'}`}
                            />
                          ))}
                        </div>
                        <span className="text-sm text-gray-500 dark:text-gray-400">{dateStr}</span>
                      </div>
                    </div>
                  </div>
                  {comment && <p className="text-gray-600 dark:text-gray-300 text-sm">{comment}</p>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}
